import { useCallback, useEffect, useRef } from 'react';
import { Sample } from '../../@types/samples/types';
import type {
  WorkerMessage,
  WorkerResponse,
} from '../../components/dashboard/components/LiaoAnalysis/hooks/useLiaoAnalysis/liao.worker';

/**
 * Shared Liao worker for the Multi Analysis detail panel.
 *
 * The backend does not persist the `analysis_curves.liao` scatter arrays
 * (PceMatch/Pres/CorrectedSw), so samples loaded from the database need one
 * worker pass before their fit-vs-lab chart can be drawn. Requests are
 * serialized through a promise chain (one in-flight worker message at a
 * time) and stamped with a request id so a stale response — e.g. the user
 * stepped to the next sample while the previous one was still computing —
 * resolves to `null` instead of overwriting fresher data.
 */
export function useLiaoWorkerQueue() {
  const workerRef = useRef<Worker | null>(null);
  const queueRef = useRef<Promise<unknown>>(Promise.resolve());
  const requestIdRef = useRef(0);

  useEffect(() => {
    workerRef.current = new Worker(
      new URL(
        '../../components/dashboard/components/LiaoAnalysis/hooks/useLiaoAnalysis/liao.worker.ts',
        import.meta.url
      ),
      { type: 'module' }
    );

    return () => {
      workerRef.current?.terminate();
      workerRef.current = null;
    };
  }, []);

  const postToWorker = useCallback(
    (message: WorkerMessage): Promise<Sample> => {
      const worker = workerRef.current;
      if (!worker) {
        return Promise.reject(new Error('Worker not initialized'));
      }

      return new Promise((resolve, reject) => {
        const handleMessage = (event: MessageEvent<WorkerResponse>) => {
          const data = event.data as WorkerResponse & {
            __error__?: true;
            message?: string;
          };
          cleanup();
          if (data && (data as { __error__?: true }).__error__) {
            reject(new Error(data.message ?? 'Error calculating Liao curves'));
            return;
          }
          resolve(data);
        };

        const handleError = (event: ErrorEvent) => {
          cleanup();
          reject(new Error(event.message));
        };

        const cleanup = () => {
          worker.removeEventListener('message', handleMessage);
          worker.removeEventListener('error', handleError);
        };

        worker.addEventListener('message', handleMessage);
        worker.addEventListener('error', handleError);
        worker.postMessage(message);
      });
    },
    []
  );

  /**
   * Regenerate `analysis_curves.liao` for a sample. Resolves `null` when the
   * request became stale (a newer request superseded it) or on failure.
   */
  const regenerateCurves = useCallback(
    (sample: Sample): Promise<Sample | null> => {
      requestIdRef.current += 1;
      const requestId = requestIdRef.current;
      const liao = sample.analysis_values?.liao;
      if (!liao) return Promise.resolve(null);

      const message: WorkerMessage = {
        sample,
        input: {
          custom_a: liao.best_a || 0,
          custom_b: liao.best_b || 0,
          pce: liao.pce || 0,
          large_derivative: liao.large_derivative || 0,
        },
      };

      const request = queueRef.current
        .catch(() => undefined)
        .then(() => postToWorker(message))
        .then(result => (requestIdRef.current === requestId ? result : null))
        .catch(() => null);
      queueRef.current = request;
      return request;
    },
    [postToWorker]
  );

  return { regenerateCurves };
}
